/* eslint-disable unused-imports/no-unused-imports */
//@ts-check
import {
  ConnectionStatus,
  DeviceLocationPermission,
  SubscriptionTier,
} from '../../../generated-client/models';

/**
 * View model for the UserStatusPing class.
 * Contains business logic related to user status.
 */
export class UserStatusPingViewModel {
  /**
   * @param {import('../../../generated-client/models').UserStatusPing} userStatusPingDTO
   */
  constructor(userStatusPingDTO) {
    /** @type {import('../../../generated-client/models').UserStatusPing} */
    this.dto = userStatusPingDTO;
    /** @type {import('../../../generated-client/models').UserLocationRequest|null} The user's location information */
    this.location = userStatusPingDTO?.location || null;
  }

  /**
   * Checks if the user's location is being shared (location timestamp within 30 seconds of ping timestamp).
   * @returns {boolean}
   */
  isSharingLocation() {
    var status = this.getCalculatedConnectionStatus();
    if (
      status &&
      status !== ConnectionStatus.Unknown &&
      status !== ConnectionStatus.DisconnectedIntentional &&
      this.location &&
      typeof this.location.timestamp === 'number' &&
      this.dto.timestamp - this.location.timestamp <= 3 * 60 // 3 minutes
    ) {
      return true;
    }
    return false;
  }

  /**
   * Calculates and returns the user's connection status.
   * If the user should appear as disconnected, returns ConnectionStatus.UNKNOWN.
   * If the current connection status is UNKNOWN, but shouldn't show as such, it returns undefined.
   * Eventually the backend should handle this logic and this method can be removed.
   *
   * @returns {ConnectionStatus | undefined} The calculated connection status or undefined.
   */
  getCalculatedConnectionStatus() {
    // Determine if the user should be shown as disconnected
    // User should show as disconnected if their connection status is UNKNOWN
    // and their last ping timestamp is newer than 24 hours
    const shouldShowAsDisconnected =
      this.dto.connection_status === ConnectionStatus.Unknown &&
      this.dto.timestamp > Date.now() / 1000 - 24 * 60 * 60; // 24 hours in seconds
    if (shouldShowAsDisconnected) {
      return ConnectionStatus.Unknown;
    } else if (this.dto.connection_status === ConnectionStatus.Unknown) {
      return undefined;
    }
    return this.dto.connection_status;
  }

  /**
   * Checks if the user's device has location permission issues.
   * Returns true if the device_location_permission is not set to 'all_the_time'.
   *
   */
  hasDeviceLocationPermissionIssue() {
    return (
      this.dto.device_info &&
      this.dto.device_info.device_location_permission !==
        DeviceLocationPermission.AllTheTime
    );
  }

  /**
   * Checks if the user's device has notification permission issues.
   * Returns true if all_notifications_enabled is false (disabled).
   */
  hasDeviceNotificationPermissionIssue() {
    return (
      this.dto.device_info &&
      this.dto.device_info.all_notifications_enabled === false
    );
  }

  /**
   * Combined permission issue (location OR notifications).
   */
  hasAnyDevicePermissionIssue() {
    return Boolean(
      this.hasDeviceLocationPermissionIssue() ||
        this.hasDeviceNotificationPermissionIssue(),
    );
  }

  /**
   * Checks if the user's location is outdated (older than 24 hours).
   * Compares the location timestamp to the current time instead of dto timestamp.
   * @returns {boolean}
   */
  isLocationOutdated() {
    return (
      this.location !== null &&
      typeof this.location.timestamp === 'number' &&
      Date.now() / 1000 - this.location.timestamp > 24 * 60 * 60 // 24 hours
    );
  }

  /**
   * Determines if a pin should be shown on the map for this user.
   * A pin is shown if the user is sharing their location or if they are disconnected.
   * @returns {boolean}
   */
  shouldShowPinOnMap() {
    if (
      this.location == null ||
      this.location.latitude == null ||
      this.location.longitude == null
    ) {
      return false;
    }
    if (this.isSharingLocation()) {
      return true;
    }
    if (this.isDisconnected()) {
      return !this.isLocationOutdated();
    }
    return false;
  }

  /**
   * Checks if the user is considered disconnected.
   * @returns {boolean}
   */
  isDisconnected() {
    const status = this.getCalculatedConnectionStatus();
    return status === ConnectionStatus.Unknown;
  }

  /**
   * Checks if the user is considered active - the same statuses `UserStatusChip` labels
   * "Active" on the Map and User Dashboard.
   * @returns {boolean}
   */
  isActive() {
    const status = this.getCalculatedConnectionStatus();
    return (
      status === ConnectionStatus.Active ||
      status === ConnectionStatus.CheckingConnection
    );
  }

  /**
   * Checks if the user currently holds a paid (Ops or Respond) subscription tier.
   * Mirrors the API's SubscriptionTierService.isLocationVisibleOnGeneralMap rule: a missing or
   * unrecognized tier is treated as unpaid (fail closed).
   * @returns {boolean}
   */
  isPaidSubscriber() {
    return (
      this.dto?.effective_tier === SubscriptionTier.Ops ||
      this.dto?.effective_tier === SubscriptionTier.Respond
    );
  }
}
